// Shared helpers for the League Finance views.

import {
  LayoutDashboard, ShieldCheck, Gavel, Receipt
} from "lucide-react";

export const FIN_TABS = [
  { key: "overview", label: "Balance Sheet",     icon: LayoutDashboard },
  { key: "teamFees", label: "Team Fees",         icon: ShieldCheck },
  { key: "umpiring", label: "Umpiring Payments", icon: Gavel },
  { key: "expenses", label: "Expenses",          icon: Receipt }
];

export const WRITE_ROLES = ["ADMIN", "PRESIDENT", "TREASURER"];
export function canWriteFinance(user) {
  return (user?.roles || []).some(r => WRITE_ROLES.includes(r));
}

export const TEAM_FEE_CATEGORIES = [
  { code: "Registration",     label: "Team Registration",     tone: "gold"   },
  { code: "BallPayment",      label: "Additional Ball",       tone: "cyan"   },
  { code: "AdditionalPlayer", label: "Additional Player Reg", tone: "green"  },
  { code: "PracticeBalls",    label: "Practice Balls",        tone: "violet" },
  { code: "Fine",             label: "Fine",                  tone: "red"    }
];
export const CATEGORY_LABEL = code =>
  (TEAM_FEE_CATEGORIES.find(c => c.code === code) || { label: code }).label;
export const CATEGORY_TONE = code =>
  (TEAM_FEE_CATEGORIES.find(c => c.code === code) || { tone: "gold" }).tone;

// Common expense categories seeded from the 2026 workbook — used as a
// suggestion list on the entry form; free-text still allowed.
export const EXPENSE_CATEGORIES = [
  "Rental", "Match Balls", "Trophies", "Equipment", "Meeting Logistics",
  "Google Space", "Waiver", "Supplies", "Bank Charges", "Food",
  "Award Night", "Misc", "CricClub", "Ball Payment"
];

// Payment modes we've seen — datalist suggestions, still free-text.
export const PAYMENT_MODES = [
  "Cash", "Zelle", "Check", "Check+Zelle", "Venmo",
  "Internal Transfer", "Wire", "PayPal", "CashApp"
];

export function fmtMoney(n, opts = {}) {
  if (n == null || Number.isNaN(Number(n))) return "$0.00";
  const val = Number(n);
  const showCents = opts.cents !== false;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: showCents ? 2 : 0,
    maximumFractionDigits: showCents ? 2 : 0
  }).format(val);
}

export function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00");
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-US", {
    month: "short", day: "numeric", year: "numeric"
  });
}

export function todayIso() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

// Colored dot palette used to differentiate category rows.
export const CATEGORY_TONES = {
  gold:   "#f7c948",
  cyan:   "#3ed3e0",
  green:  "#43d893",
  violet: "#b07cff",
  orange: "#ff8a2a",
  red:    "#ff6b6b"
};

// Umpire rates are scoped to a ground. The schedule uses these coarse
// buckets ("Linbrook" or "Clarion"); individual matches may live under
// sub-grounds like CC-1 / CC-2 which we roll up to "Clarion".
export const UMPIRE_RATE_GROUNDS = ["Linbrook", "Clarion"];

export function groundBucket(matchGround) {
  if (!matchGround) return null;
  const g = String(matchGround).trim().toLowerCase();
  if (g.startsWith("linbrook")) return "Linbrook";
  if (g.startsWith("cc") || g.includes("clarion")) return "Clarion";
  return null;
}
